import { makeCygnusApiCall } from "../../../api/api";
import { clientResponseRowsToMap } from "../../client_onboarding/ClientEditor/ClientEditor.service";

/**
 * Fetches wizard page templates for the selected engagement letter template.
 * @param templateId - The ID of the selected template
 * @returns The wizard form page configuration
 */
export const fetchEngagementForms = async (
  templateId: number
): Promise<any> => {
  if (!templateId) {
    throw new Error("Template ID is undefined");
  }

  const response = await makeCygnusApiCall({
    type: "GET",
    url: `/engagement-letter/el_forms/?TemplateId=${templateId}`,
    show_message: true,
    customized_success_message: "Fetched wizard page details.",
    customized_failure_message: "Unable to fetch wizard page details.",
    customized_error_message:
      "Error occurred while fetching wizard pages. Please try again or contact support.",
    isblob: false,
    forMDM: true,
  });

  if (response?.data) {
    return response.data;
  } else {
    throw new Error("No data received from EL forms API");
  }
};

/**
 * Fetches the base64-encoded content of the engagement letter for a given template.
 * @param templateId - The ID of the engagement letter template
 * @returns Base64 string of the letter content
 */
export const fetchELBase64 = async (templateId: number): Promise<string> => {
  if (!templateId) {
    throw new Error("Template ID is undefined");
  }

  const response = await makeCygnusApiCall({
    type: "POST",
    url: "/engagement-letter/merge_template_content/",
    payload: { TemplateId: templateId },
    show_message: false,
    customized_success_message: "Fetched engagement letter base64.",
    customized_failure_message: "Unable to fetch base64 engagement letter.",
    customized_error_message:
      "Error occurred while fetching the engagement letter. Please try again.",
    isblob: false,
    forMDM: true,
  });

  if (response?.data?.Base64Data) {
    return response.data.Base64Data;
  } else {
    throw new Error("Base64Data not found in response");
  }
};

/**
 * Fetches all mapped fields related to a selected template.
 * @param templateId - The ID of the engagement letter template
 * @returns Mapped field structure for the template
 */
export const fetchTemplateFieldDetails = async (
  templateId: number
): Promise<any> => {
  if (!templateId) {
    throw new Error("Template ID is undefined");
  }

  const response = await makeCygnusApiCall({
    type: "GET",
    url: `/engagement-letter/template_field_mappings?TemplateId=${templateId}`,
    payload: {},
    show_message: false,
    customized_success_message: "Fetched template field mappings.",
    customized_failure_message: "Unable to fetch template field mappings.",
    customized_error_message:
      "Error occurred while fetching template field mappings. Please try again.",
    isblob: false,
    forMDM: true,
  });

  if (response?.data?.length > 0) {
    return response.data[0];
  } else {
    throw new Error("Template field data not found in response");
  }
};

/**
 * Fetches rule configuration and details for a specific rule ID.
 * @param ruleId - The ID of the rule
 * @returns Full rule configuration object
 */
export const fetchRuleDetails = async (ruleId: number): Promise<any> => {
  if (ruleId === undefined) {
    throw new Error("Rule ID is undefined");
  }

  const response = await makeCygnusApiCall({
    type: "GET",
    url: `/engagement-letter/template_rule_details/?TemplateRuleId=${ruleId}`,
    show_message: false,
    customized_success_message: "Rule details fetched successfully.",
    customized_failure_message:
      "Unable to fetch rule details, please try again later.",
    customized_error_message: "An error occurred while fetching rule details.",
    isblob: false,
    forMDM: true,
  });

  if (response.success && response.data?.length > 0) {
    return response.data[0];
  } else {
    throw new Error("Failed to fetch rule details.");
  }
};

/**
 * Submits the final engagement letter data to the backend for saving.
 * @param templateId - The selected template ID
 * @param clientMasterId - The client master ID
 * @param clientDetails - Mapped client field data
 * @param docFileData - Base64 content of the final document
 * @param onHide - Callback to hide the modal upon success
 */
export const saveEngagementLetter = async (
  templateId: number,
  clientMasterId: number | undefined,
  clientDetails: any,
  contentBlocks: any,
  docFileData: any,
  recipients: any,
  onHide: any,
  isDraft?: boolean,
  processedTemplateId?: number
): Promise<any> => {
  const response = await makeCygnusApiCall({
    type: "POST",
    url: "/engagement-letter/processed_templates/",
    payload: {
      TemplateId: templateId,
      ClientMasterId: clientMasterId,
      Status: "Completed",
      ClientDetails: clientDetails,
      ContentBlocks: contentBlocks,
      DocFileData: docFileData,
      Recipients: recipients,
      IsDraft: isDraft ?? false,
      ...(processedTemplateId ? { ProcessedTemplateId: processedTemplateId } : {}),
    },
    show_message: true,
    customized_success_message: isDraft
      ? "Engagement Letter saved as draft!"
      : "Engagement Letter saved successfully!",
    customized_failure_message: "Failed to save engagement letter",
    customized_error_message:
      "An error occurred while saving the engagement letter.",
    isblob: false,
    forMDM: true,
  });

  if (response.success) {
    onHide();
  }
};

/**
 * Performs a mail merge operation for an engagement letter template.
 *
 * Sends the given template ID, content blocks, rules, and field data
 * to the backend API, which generates a merged document and returns
 * its base64-encoded representation.
 *
 * @param templateId - Unique identifier of the engagement letter template.
 * @param contentBlocks - (Optional) List of content blocks to include/exclude
 *                        based on their `enabled` state.
 * @param rules - (Optional) Rule configurations to apply during merge.
 * @param fieldsData - (Optional) Field mappings and values used for substitution.
 *
 * @returns A Promise that resolves to the base64 string of the merged document,
 *          or `null` if no data is returned from the API.
 */
export const mailMerge = async (
  templateId: number,
  contentBlocks?: any,
  rules?: any,
  fieldsData?: any,
  base64Data?: any
): Promise<any> => {
  const response = await makeCygnusApiCall({
    type: "POST",
    url: "/engagement-letter/mail_merge/",
    payload: {
      TemplateId: templateId,
      ContentBlocks: contentBlocks,
      Rules: rules,
      FieldsData: fieldsData,
      Base64Data: base64Data,
    },
    show_message: false,
    customized_success_message: "",
    customized_failure_message: "",
    customized_error_message:
      "",
    isblob: false,
    forMDM: true,
  });

  return response?.data?.Base64Data ?? null;
};

/**
 * Fetches client form responses using the clientMasterId.
 * @param clientMasterId - The ID of the client
 * @returns Client's response object with field values
 */
export const fetchClientDetails = async (
  clientMasterId: number | undefined
) => {
  if (!clientMasterId) {
    return;
  }
  const response = await makeCygnusApiCall({
    type: "POST",
    payload: {
      ClientMasterId: clientMasterId,
    },
    url: "/form/response/",
    show_message: true,
    customized_success_message: "",
    customized_failure_message: "Failed to fetch client data.",
    customized_error_message: "Error occurred while fetching client data.",
    isblob: false,
    forMDM: true,
  });

  if (response.success) {
    return clientResponseRowsToMap(response.data);
  }
};

/**
 * Fetches all wizard page data in a single response.
 * @param templateId - The ID of the engagement letter template
 * @returns wizard page complete data
 */
export const fetchWizardPageData = async (
  templateId: number,
  clientData?: any,
  processedTemplateId?: number
): Promise<any> => {
  if (!templateId) {
    throw new Error("Template ID is undefined");
  }

  const response = await makeCygnusApiCall({
    type: "POST",
    url: `/engagement-letter/wizard_page_data/`,
    payload: {
      TemplateId: templateId,
      ClientData: clientData,
      ...(processedTemplateId ? { ProcessedTemplateId: processedTemplateId } : {}),
    },
    show_message: false,
    customized_success_message: "Fetched template wizard data.",
    customized_failure_message: "Unable to fetch template wizard data.",
    customized_error_message:
      "Error occurred while fetching template wizard data. Please try again.",
    isblob: false,
    forMDM: true,
  });

  if (response?.data) {
    return response.data;
  } else {
    throw new Error("Template wizard data not found in response");
  }
};

/**
 * Fetches saved field values from the processed template client details vertical table.
 * @param processedTemplateId - The ID of the processed template
 * @returns Array of { ClientFieldName, ClientFieldValue, ... } records
 */
export const fetchProcessedTemplateClientDetails = async (
  processedTemplateId: number
): Promise<Array<{ FieldCode?: string; ClientFieldName: string | null; ClientFieldValue: any }>> => {
  const response = await makeCygnusApiCall({
    type: "GET",
    url: `/engagement-letter/processed_template_client_details/?ProcessedTemplateId=${processedTemplateId}`,
    show_message: false,
    customized_success_message: "",
    customized_failure_message: "Unable to fetch saved field values.",
    customized_error_message: "Error fetching saved field values.",
    isblob: false,
    forMDM: true,
  });

  return response?.data ?? [];
};
